import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useHorizontalSwipe } from '@/hooks/useHorizontalSwipe';
import { useOnboardingState } from '@/hooks/useOnboardingState';
import { useOptimizedFamily } from '@/hooks/useOptimizedFamily';
import { OnboardingLayout } from '@/components/onboarding/OnboardingLayout';
import { OnboardingStep1 } from '@/components/onboarding/OnboardingStep1';
import { OnboardingStep2 } from '@/components/onboarding/OnboardingStep2';
import { OnboardingStep3 } from '@/components/onboarding/OnboardingStep3';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useQueryClient } from '@tanstack/react-query';
import { generateDemoContent } from '@/utils/demoContentGenerator';
import { useNativeConfirmation } from '@/hooks/useNativeConfirmation';

export default function NewUserOnboarding() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();
  const { 
    currentStep, 
    data, 
    updateData, 
    nextStep, 
    prevStep, 
    goToStep, 
    canProceed 
  } = useOnboardingState();

  // Removed unused hooks that were causing unnecessary re-renders
  // const { createWorkspace, loading: workspaceLoading, refreshData: refreshWorkspaces } = useWorkspaces();
  // const { createPerson, loading: personLoading } = usePersonProfiles();
  const { family, families, createFamily, refreshData: refreshFamily } = useOptimizedFamily();
  const queryClient = useQueryClient();
  const { showConfirmation } = useNativeConfirmation();

  // Handle swipe gestures for navigation
  const { handleTouchStart, handleTouchMove, handleTouchEnd } = useHorizontalSwipe({
    onSwipeLeft: () => {
      if (canProceed() && currentStep < 3) {
        nextStep();
      }
    },
    onSwipeRight: () => {
      if (currentStep > 1) {
        prevStep();
      }
    },
    threshold: 50
  });

  // Redirect if not authenticated
  useEffect(() => {
    if (!user) {
      navigate('/auth');
      return;
    }

    const onboardingMode = localStorage.getItem('onboardingMode');
    const existingFamilyId = localStorage.getItem('onboardingFamilyId');
    
    console.log('🔍 Onboarding init check:', { 
      onboardingMode, 
      existingFamilyId
    });

    // If no mode set, default to new family creation
    if (!onboardingMode) {
      localStorage.setItem('onboardingMode', '1');
      console.log('🆕 Onboarding mode missing. Defaulting to new family creation ("1")');
      return;
    }

    // For new family creation mode, just clear conflicting data
    if (onboardingMode === '1') {
      console.log('🆕 New family creation mode - clearing conflicting family ID');
      localStorage.removeItem('onboardingFamilyId');
      return;
    }

    // For existing family mode, check if onboarding is actually needed
    if (onboardingMode === 'existing' && existingFamilyId && family?.id === existingFamilyId && family?.name) {
      console.log('📝 Existing family onboarding mode - checking if needed');
      updateData({ familyName: family.name });
      
      const checkOnboardingNeed = async () => {
        const { isOnboardingNeeded } = await import('@/utils/onboardingUtils');
        const needsOnboarding = await isOnboardingNeeded(family.id, family.name);
        
        if (!needsOnboarding) {
          console.log('✅ Family already configured, redirecting to dashboard');
          localStorage.removeItem('onboardingFamilyId');
          localStorage.removeItem('onboardingMode');
          navigate('/dashboard');
        }
      };
      
      checkOnboardingNeed();
    }
  }, [user, navigate]); // Minimal dependencies

  // Add guard to prevent multiple simultaneous calls
  const [isCreatingFamily, setIsCreatingFamily] = useState(false);

  const handleFinishOnboarding = async () => {
    if (isCreatingFamily) {
      console.log('⚠️  Family creation already in progress, skipping...');
      return;
    }

    try {
      setIsCreatingFamily(true);
      console.log('🚀 Starting family creation process...');

      // Check if we're updating an existing family from onboarding
      const existingFamilyId = localStorage.getItem('onboardingFamilyId');
      let targetFamilyId: string;
      
      if (existingFamilyId && family?.id === existingFamilyId) {
        console.log('📝 Updating existing family:', existingFamilyId);
        // Update existing family name if different
        if (family.name !== data.familyName) {
          const { error: updateError } = await supabase
            .from('families')
            .update({ name: data.familyName })
            .eq('id', existingFamilyId);
            
          if (updateError) {
            console.error('Error updating family name:', updateError);
            throw updateError;
          }
        }
        targetFamilyId = existingFamilyId;
        localStorage.removeItem('onboardingFamilyId');
      } else {
        console.log('🆕 Creating new family:', data.familyName);
        
        // Use direct Supabase RPC to get the new family ID immediately
        const { data: familyData, error: familyError } = await supabase
          .rpc('create_family_with_membership', {
            family_name: data.familyName || 'Ma Famille'
          });

        if (familyError) {
          console.error('Error creating family:', familyError);
          throw familyError;
        }

        targetFamilyId = (familyData as any)?.id;
        if (!targetFamilyId) {
          throw new Error('Failed to get new family ID from creation response');
        }
        
        console.log('✅ New family created with ID:', targetFamilyId);
        
        // Immediately set the new family as current to avoid hook staleness
        localStorage.setItem('currentFamilyId', targetFamilyId);
        
        // Clear all family-related caches to prevent contamination
        queryClient.invalidateQueries({ queryKey: ['family-data'] });
        queryClient.invalidateQueries({ queryKey: ['person-profiles'] });
        queryClient.invalidateQueries({ queryKey: ['events'] });
        queryClient.invalidateQueries({ queryKey: ['tasks'] });
      }

      console.log('🎯 Target family ID:', targetFamilyId);

      // Get current user's profile ID for direct inserts
      const { data: profileData } = await supabase
        .from('profiles')
        .select('id')
        .eq('user_id', user.id)
        .single();

      if (!profileData?.id) {
        throw new Error('Profile non trouvé');
      }

      // 2.5. Update family photo if provided
      if (data.familyPhoto) {
        await supabase
          .from('families')
          .update({ background_image_url: data.familyPhoto })
          .eq('id', targetFamilyId);
      }

      // 3. Create spouse if provided - direct insert to ensure correct family_id
      if (data.spouseName.trim()) {
        await supabase
          .from('person_profiles')
          .insert({
            family_id: targetFamilyId,
            first_name: data.spouseName,
            last_name: '',
            category: 'family',
            family_role: 'parent',
            avatar_url: data.spousePhoto,
            created_by: profileData.id
          });
      }

      // 4. Create children if provided - direct insert to ensure correct family_id
      if (data.children.length > 0) {
        for (let i = 0; i < data.children.length; i++) {
          const childName = data.children[i];
          if (childName.trim()) {
            await supabase
              .from('person_profiles')
              .insert({
                family_id: targetFamilyId,
                first_name: childName,
                last_name: '',
                category: 'family',
                family_role: 'child',
                avatar_url: data.childrenPhotos[i],
                created_by: profileData.id
              });
          }
        }
      }

      // Clean up onboarding state BEFORE navigation
      console.log('🧹 Cleaning up onboarding data after successful creation');
      localStorage.removeItem('onboardingMode');
      localStorage.removeItem('onboardingFamilyId');
      sessionStorage.removeItem('isNewUserSignup'); // Clear new user signup flag
      sessionStorage.removeItem('hasRedirectedToOnboarding'); // Clear redirect flag

      // Pas de notification pendant l'onboarding pour éviter les distractions

      // Selective cache invalidation instead of clearing everything
      queryClient.invalidateQueries({ queryKey: ['family-data'] });
      queryClient.invalidateQueries({ queryKey: ['user-families'] });
      queryClient.invalidateQueries({ queryKey: ['person-profiles'] });
      console.log('🔄 Cache selectively invalidated');
      
      // Automatically generate demo content (no confirmation needed)
      if (targetFamilyId) {
        try {
          console.log('🎭 Generating demo content automatically...');
          
          // Mark family as having demo content first
          await supabase
            .from('families')
            .update({ demo_content_active: true })
            .eq('id', targetFamilyId);
          
          // Prepare family members array for demo generation
          const familyMembersForDemo = [];
          
          // Fetch created person profiles for this family
          const { data: personProfiles } = await supabase
            .from('person_profiles')
            .select('id, first_name, last_name, family_role, date_of_birth')
            .eq('family_id', targetFamilyId);
          
          // Add current user profile
          familyMembersForDemo.push({
            id: profileData.id,
            firstName: user?.user_metadata?.first_name || 'Parent',
            lastName: user?.user_metadata?.last_name || data.familyName.split(' ')[0] || 'Famille',
            role: 'parent',
          });
          
          // Add person profiles (spouse and children)
          if (personProfiles && personProfiles.length > 0) {
            personProfiles.forEach(person => {
              familyMembersForDemo.push({
                id: person.id,
                firstName: person.first_name,
                lastName: person.last_name,
                role: person.family_role || 'child',
                dateOfBirth: person.date_of_birth || undefined,
              });
            });
          }
          
          await generateDemoContent({
            familyId: targetFamilyId,
            familyMembers: familyMembersForDemo,
            familyName: data.familyName,
            currentProfileId: profileData.id,
          });
          
          console.log('✅ Demo content generated successfully');
          
          // CRITICAL: Invalidate all caches to ensure fresh data is loaded
          await queryClient.invalidateQueries({ queryKey: ['family'] });
          await queryClient.invalidateQueries({ queryKey: ['tasks'] });
          await queryClient.invalidateQueries({ queryKey: ['events'] });
          await queryClient.invalidateQueries({ queryKey: ['documents'] });
          await queryClient.invalidateQueries({ queryKey: ['persons'] });
          
          console.log('🔄 All caches invalidated after demo content generation');
        } catch (demoError) {
          console.error('❌ Error generating demo content:', demoError);
          // Don't block navigation if demo generation fails
          toast({
            title: 'Avertissement',
            description: 'Le contenu de démonstration n\'a pas pu être généré, mais votre famille a été créée avec succès.',
            variant: 'destructive',
          });
        }
      }
      
      // Smooth navigate to dashboard and let it show its own loader
      console.log('🏠 Navigating to dashboard...');
      navigate('/dashboard', { replace: true });
    } catch (error) {
      console.error('❌ Error during onboarding:', error);
      setIsCreatingFamily(false); // Reset guard on error
      toast({
        title: "Erreur",
        description: "Une erreur est survenue lors de la création de votre famille",
        variant: "destructive",
      });
    }
  };

  if (!user) {
    return null; // Will redirect to auth
  }

  

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <OnboardingStep1
            familyName={data.familyName}
            familyPhoto={data.familyPhoto}
            onUpdate={(name) => updateData({ familyName: name })}
            onPhotoUpdate={(photo) => updateData({ familyPhoto: photo })}
          />
        );
      case 2:
        return (
          <OnboardingStep2
            spouseName={data.spouseName}
            spousePhoto={data.spousePhoto}
            onUpdate={(name) => updateData({ spouseName: name })}
            onPhotoUpdate={(photo) => updateData({ spousePhoto: photo })}
          />
        );
      case 3:
        return (
          <OnboardingStep3
            children={data.children}
            childrenPhotos={data.childrenPhotos}
            onUpdate={(children) => updateData({ children })}
            onPhotoUpdate={(index, photo) => updateData({ 
              childrenPhotos: { ...data.childrenPhotos, [index]: photo || '' }
            })}
          />
        );
      default:
        return null;
    }
  };

  const renderFooter = () => {
    if (currentStep === 1) {
      return (
        <Button 
          onClick={nextStep}
          disabled={!canProceed()}
          className="w-full h-12 bg-[hsl(var(--violet-primary))] text-white hover:bg-[hsl(var(--violet-primary))]/90"
          size="lg"
        >
          Continuer
        </Button>
      );
    }

    if (currentStep === 2) {
      return (
        <div className="flex items-center gap-3">
          <ArrowLeft 
            className="h-6 w-6 text-foreground cursor-pointer hover:text-primary transition-colors" 
            onClick={prevStep}
          />
          
          <Button 
            onClick={() => {
              // Clear spouse data when skipping
              updateData({ spouseName: '', spousePhoto: undefined });
              nextStep();
            }}
            variant="ghost"
            className="h-12 text-muted-foreground hover:text-foreground"
            size="lg"
          >
            Passer
          </Button>
          
          <Button 
            onClick={nextStep}
            className="flex-1 h-12 bg-[hsl(var(--violet-primary))] text-white hover:bg-[hsl(var(--violet-primary))]/90"
            size="lg"
          >
            Continuer
          </Button>
        </div>
      );
    }

    if (currentStep === 3) {
      return (
        <div className="flex items-center gap-3">
          <ArrowLeft 
            className="h-6 w-6 text-foreground cursor-pointer hover:text-primary transition-colors" 
            onClick={prevStep}
          />
          
          <Button 
            onClick={handleFinishOnboarding}
            className="flex-1 h-12 bg-[hsl(var(--violet-primary))] text-white hover:bg-[hsl(var(--violet-primary))]/90"
            size="lg"
            disabled={isCreatingFamily}
          >
            {isCreatingFamily ? (
              <span className="flex items-center justify-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                Création...
              </span>
            ) : (
              'Terminer'
            )}
          </Button>
        </div>
      );
    }

    return null;
  };

  return (
    <div 
      className="h-screen"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      <OnboardingLayout 
        currentStep={currentStep} 
        totalSteps={3}
        footer={renderFooter()}
      >
        {renderCurrentStep()}
      </OnboardingLayout>
    </div>
  );
}